import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '@up2d8/ui';
import { problemKind } from '../../shared/problem-text';
import { useProblemText } from '../../shared/use-problem-text';
import { Btn, Note } from '../admin-kit';
import './opvolging.css';

/**
 * Wat de vijf schermen van Opvolging (BHR-7) delen en wat niet in de admin-kit staat.
 *
 * Bewust hier en niet in de kit: acht delen van het portaal werden tegelijk gebouwd, en de kit is
 * van iedereen. De iconen en `FilterSelect` zijn in OPR-2 naar de kit verhuisd.
 */

/**
 * Laden, binnen een kaart: de grijze regels van het portaal, met de padding van de kaart. `bare`
 * laat de padding weg, voor binnen een `CardPad`.
 */
export function CardLoading({ rows = 3, bare }: { rows?: number; bare?: boolean }) {
  const { t } = useTranslation();
  const state = <LoadingState rows={rows} label={t('common.loading')} />;
  return bare ? state : <div className="a-card-pad">{state}</div>;
}

/**
 * Een lijst die niet opgehaald kon worden: het rode vlak met "Opnieuw proberen".
 *
 * De vorm van `ListError` (Extra 2, punt 14) in de stijl van het nieuwe portaal: "er is nog niets"
 * en "we weten het niet" blijven twee verschillende dingen.
 */
export function LoadError({
  onRetry,
  children,
  error,
}: {
  onRetry: () => void;
  children?: ReactNode;
  /** De fout zelf: een ontbrekend recht wordt dan uitgelegd, zonder "Opnieuw proberen" (FBK-2). */
  error?: unknown;
}) {
  const { t } = useTranslation();
  const text = useProblemText();
  const denied = problemKind(error) === 'denied';
  return (
    <div className="a-card-pad" data-testid="list-error">
      <Note tone="danger" role="alert">
        {denied ? text(error, '') : (children ?? t('errors.listFailed'))}
        {denied ? null : (
          <div style={{ marginTop: 8 }}>
            <Btn variant="sec" size="sm" onClick={onRetry}>
              {t('common.retry')}
            </Btn>
          </div>
        )}
      </Note>
    </div>
  );
}
